export type ReportFieldSourceGroup =
  | 'customer'
  | 'device'
  | 'case'
  | 'task'
  | 'work_report'
  | 'technician'
  | 'signature';

export type ReportCellId =
  | 'r1c1'
  | 'r1c2'
  | 'r1c3'
  | 'r2c1'
  | 'r2c2'
  | 'r2c3'
  | 'r3c1'
  | 'r3c2'
  | 'r3c3'
  | 'r4c1'
  | 'r4c2'
  | 'r4c3'
  | 'r5c1'
  | 'r5c2'
  | 'r5c3';

export type ReportBlockKind = 'data' | 'report_body' | 'signature';
export type ReportBodyMode = 'generated' | 'manual';
export type SignatureMode = 'screen_or_print' | 'print_only';

export type ReportFieldTemplate = {
  id: string;
  label: string;
  sourceGroup: ReportFieldSourceGroup;
  sourceKey: string;
  included: boolean;
};

export type ReportBlockTemplate = {
  id: string;
  label: string;
  description: string;
  sourceGroup: ReportFieldSourceGroup;
  included: boolean;
  kind: ReportBlockKind;
  reportBodyMode?: ReportBodyMode;
  signatureMode?: SignatureMode;
  fields: ReportFieldTemplate[];
};

export type ReportTemplate = {
  id: string;
  name: string;
  isPrimary: boolean;
  blocks: ReportBlockTemplate[];
  cellAssignments: Record<ReportCellId, string | null>;
};

export type ServiceReportDocument = {
  version?: number;
  kind?: string;
  title?: string;
  generatedAt?: string | null;
  savedAt?: string | null;
  template?: ReportTemplate | null;
  data?: Record<string, unknown>;
};

export type ServiceReportRecord = {
  id: string;
  caseId: string;
  taskId: string;
  taskRunId: string;
  templateId?: string | null;
  templateName?: string | null;
  title: string;
  reportSummary?: string | null;
  status: string;
  savedByUserId?: string | null;
  savedByName?: string | null;
  savedAt?: string | null;
  sentAt?: string | null;
  sentByName?: string | null;
  updatedAt?: string | null;
  document: ServiceReportDocument;
};

export type PreviewRegion = {
  id: string;
  block: ReportBlockTemplate | null;
  rowStart: number;
  rowEnd: number;
  colStart: number;
  colEnd: number;
};

const REPORT_GRID_CELLS: Array<{ id: ReportCellId; row: number; col: number }> = [
  { id: 'r1c1', row: 1, col: 1 },
  { id: 'r1c2', row: 1, col: 2 },
  { id: 'r1c3', row: 1, col: 3 },
  { id: 'r2c1', row: 2, col: 1 },
  { id: 'r2c2', row: 2, col: 2 },
  { id: 'r2c3', row: 2, col: 3 },
  { id: 'r3c1', row: 3, col: 1 },
  { id: 'r3c2', row: 3, col: 2 },
  { id: 'r3c3', row: 3, col: 3 },
  { id: 'r4c1', row: 4, col: 1 },
  { id: 'r4c2', row: 4, col: 2 },
  { id: 'r4c3', row: 4, col: 3 },
  { id: 'r5c1', row: 5, col: 1 },
  { id: 'r5c2', row: 5, col: 2 },
  { id: 'r5c3', row: 5, col: 3 }
];

function getNestedValue(source: unknown, path: string): unknown {
  if (!source || typeof source !== 'object' || !path) return null;
  return path.split('.').reduce<unknown>((acc, segment) => {
    if (!acc || typeof acc !== 'object') return null;
    return (acc as Record<string, unknown>)[segment];
  }, source);
}

function formatDateLike(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(parsed);
}

function formatReportValue(value: unknown, sourceKey: string): string {
  if (value === null || value === undefined) return 'Not provided';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'Not provided';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 'Not provided';
    if (sourceKey.toLowerCase().endsWith('at') || /^\d{4}-\d{2}-\d{2}t/i.test(trimmed)) {
      return formatDateLike(trimmed);
    }
    return trimmed;
  }
  if (Array.isArray(value)) {
    const entries = value
      .map(entry => (typeof entry === 'string' ? entry.trim() : String(entry ?? '').trim()))
      .filter(Boolean);
    return entries.length > 0 ? entries.join(', ') : 'Not provided';
  }
  return 'Not provided';
}

export function buildReportPreviewRegions(template: ReportTemplate | null | undefined): PreviewRegion[] {
  if (!template) return [];
  const assignments = template.cellAssignments || ({} as Record<ReportCellId, string | null>);
  const blocksById = (template.blocks || []).reduce<Record<string, ReportBlockTemplate>>((acc, block) => {
    acc[block.id] = block;
    return acc;
  }, {});
  const cellMap = REPORT_GRID_CELLS.reduce<Record<string, { row: number; col: number }>>((acc, cell) => {
    acc[cell.id] = { row: cell.row, col: cell.col };
    return acc;
  }, {});
  const visited = new Set<ReportCellId>();
  const regions: PreviewRegion[] = [];

  const cellIdFrom = (row: number, col: number) => `r${row}c${col}` as ReportCellId;
  const neighbors = (cellId: ReportCellId): ReportCellId[] => {
    const cell = cellMap[cellId];
    const next: ReportCellId[] = [];
    const deltas: Array<[number, number]> = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1]
    ];
    deltas.forEach(([dr, dc]) => {
      const nr = cell.row + dr;
      const nc = cell.col + dc;
      if (nr < 1 || nr > 5 || nc < 1 || nc > 3) return;
      next.push(cellIdFrom(nr, nc));
    });
    return next;
  };

  REPORT_GRID_CELLS.forEach(cell => {
    if (visited.has(cell.id)) return;
    const blockId = assignments[cell.id];
    const block = blockId ? blocksById[blockId] : null;
    const includeBlock = Boolean(block && block.included);

    if (!includeBlock) {
      visited.add(cell.id);
      regions.push({
        id: `empty-${cell.id}`,
        block: null,
        rowStart: cell.row,
        rowEnd: cell.row,
        colStart: cell.col,
        colEnd: cell.col
      });
      return;
    }

    const queue: ReportCellId[] = [cell.id];
    const component = new Set<ReportCellId>();

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      const currentBlockId = assignments[current];
      if (currentBlockId !== blockId) continue;
      visited.add(current);
      component.add(current);
      neighbors(current).forEach(nextId => {
        if (!visited.has(nextId) && assignments[nextId] === blockId) {
          queue.push(nextId);
        }
      });
    }

    const coords = Array.from(component).map(id => cellMap[id]);
    regions.push({
      id: `block-${blockId}-${cell.row}-${cell.col}`,
      block,
      rowStart: Math.min(...coords.map(entry => entry.row)),
      rowEnd: Math.max(...coords.map(entry => entry.row)),
      colStart: Math.min(...coords.map(entry => entry.col)),
      colEnd: Math.max(...coords.map(entry => entry.col))
    });
  });

  return regions;
}

export function getServiceReportFieldValue(
  document: ServiceReportDocument | null | undefined,
  field: ReportFieldTemplate
): string {
  const source = document?.data?.[field.sourceGroup] || null;
  return formatReportValue(getNestedValue(source, field.sourceKey), field.sourceKey);
}

export function getServiceReportBodyValue(
  document: ServiceReportDocument | null | undefined,
  block: ReportBlockTemplate
): string {
  const source = (document?.data?.work_report || {}) as Record<string, unknown>;
  const key = block.reportBodyMode === 'manual' ? 'manualBody' : 'generatedReport';
  return formatReportValue(source[key], key);
}

export function getServiceReportSignatureValue(
  document: ServiceReportDocument | null | undefined,
  block: ReportBlockTemplate
): string {
  const source = (document?.data?.signature || {}) as Record<string, unknown>;
  if (block.label.toLowerCase().includes('customer')) {
    return formatReportValue(source.customerLabel, 'customerLabel');
  }
  return formatReportValue(source.serviceRepresentativeLabel || source.technicianLabel, 'serviceRepresentativeLabel');
}
